import { useNavigate } from '@tanstack/react-router';
import { flexRender, type Table as TanstackTable } from '@tanstack/react-table';
import styled from 'styled-components';
import type { TableSortState } from '@/admin/ui/tableSearch';

const StyledTable = styled.table`
  background: var(--grey-50);
  background-color: var(--component-color);
  border-collapse: collapse;
  border-radius: var(--area-border-radius);
  box-shadow: var(--shadow-md);
  overflow: hidden;
  width: 100%;
`;

const Th = styled.th`
  background: var(--component-header-color);
  border-bottom: var(--component-border);
  color: var(--grey-800);
  font-size: 0.875rem;
  font-weight: 600;
  padding: var(--inner-padding);
  text-align: left;
`;

// The glyph is ::after content so the header's text stays exactly the label.
const SortButton = styled.button<{ $order?: 'asc' | 'desc' }>`
  align-items: center;
  background: none;
  border: 0;
  color: inherit;
  cursor: pointer;
  display: inline-flex;
  font: inherit;
  gap: 0.35rem;
  padding: 0;

  &::after {
    color: var(--grey-500);
    content: ${({ $order }) =>
      $order === 'asc'
        ? "'\\25B2'"
        : $order === 'desc'
          ? "'\\25BC'"
          : "'\\2195'"};
    font-size: 0.7rem;
  }
`;

const Td = styled.td`
  border-bottom: 1px solid var(--grey-200);
  color: var(--grey-800);
  font-size: 0.875rem;
  padding: 0.75rem 1rem;
`;

const Tr = styled.tr<{ $clickable?: boolean; $selected?: boolean }>`
  background: ${({ $selected }) =>
    $selected ? 'color-mix(in srgb, var(--blue) 10%, transparent)' : 'none'};
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};

  &:hover {
    background: var(--grey-200);
  }
`;

interface TableProps<
  TData,
  TParams extends Record<string, string> = Record<string, string>,
> {
  table: TanstackTable<TData>;
  to?: string;
  getParams?: (row: TData) => TParams;
  isSelected?: (row: TData) => boolean;
  // tanstack column id -> the API's sort key. A column named here renders its
  // header as a sort toggle carrying `aria-sort`; the rest stay inert.
  sortKeys?: Record<string, string>;
  sort?: TableSortState;
  onSortToggle?: (sortKey: string) => void;
}

export function Table<TData, TParams extends Record<string, string>>({
  table,
  to,
  getParams,
  isSelected,
  sortKeys,
  sort,
  onSortToggle,
}: TableProps<TData, TParams>) {
  const navigate = useNavigate();

  const handleRowClick = (row: TData) => {
    if (to && getParams) {
      navigate({ to, params: getParams(row) });
    }
  };

  const isClickable = Boolean(to && getParams);

  return (
    <StyledTable>
      <thead>
        {table.getHeaderGroups().map((headerGroup) => (
          <tr key={headerGroup.id}>
            {headerGroup.headers.map((header) => {
              const sortKey = sortKeys?.[header.column.id];
              const sortable = Boolean(sortKey && onSortToggle);
              const order =
                sortKey && sort?.key === sortKey ? sort.order : undefined;
              const label = header.isPlaceholder
                ? null
                : flexRender(
                    header.column.columnDef.header,
                    header.getContext(),
                  );
              return (
                <Th
                  key={header.id}
                  aria-sort={
                    sortable
                      ? order === 'asc'
                        ? 'ascending'
                        : order === 'desc'
                          ? 'descending'
                          : 'none'
                      : undefined
                  }
                >
                  {sortable && sortKey ? (
                    <SortButton
                      type="button"
                      $order={order}
                      onClick={() => onSortToggle?.(sortKey)}
                    >
                      {label}
                    </SortButton>
                  ) : (
                    label
                  )}
                </Th>
              );
            })}
          </tr>
        ))}
      </thead>
      <tbody>
        {table.getRowModel().rows.map((row) => (
          <Tr
            key={row.id}
            $clickable={isClickable}
            $selected={isSelected?.(row.original)}
            onClick={() => handleRowClick(row.original)}
          >
            {row.getVisibleCells().map((cell) => (
              <Td key={cell.id}>
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </Td>
            ))}
          </Tr>
        ))}
      </tbody>
    </StyledTable>
  );
}
